import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, Target, DollarSign, 
  Check, Tag, Clock, Bell, User as UserIcon, Store
} from 'lucide-react';

// Grabbit Logo
const GRABBIT_LOGO = "https://customer-assets.emergentagent.com/job_8dd61eda-1b76-4ded-9514-b53b1f23bf59/artifacts/uppz6d01_Grabbit.png";

const STEPS = ['Category', 'Product', 'Vendors', 'Alert', 'Confirm'];

const CATEGORIES = [
  { id: 'beauty', name: 'Beauty', icon: '💄', color: '#F9A8D4' },
  { id: 'clothing', name: 'Clothing', icon: '👕', color: '#7DD3FC' },
  { id: 'shoes', name: 'Shoes', icon: '👟', color: '#4ADE80' },
  { id: 'home', name: 'Home', icon: '🪑', color: '#FCD34D' },
  { id: 'tech', name: 'Tech', icon: '📱', color: '#A78BFA' },
  { id: 'travel', name: 'Travel', icon: '✈️', color: '#2DD4BF' },
  { id: 'gifts', name: 'Gifts', icon: '🎁', color: '#FB923C' },
  { id: 'flash', name: 'Flash Deals', icon: '⚡', color: '#F87171' },
];

const CATEGORY_DATA = {
  beauty: {
    brands: ['Charlotte Tilbury', 'Fenty Beauty', 'Rare Beauty', 'MAC', 'NARS', 'Glossier', 'Benefit', 'Too Faced'],
    vendors: ['Sephora', 'Ulta', 'MAC', 'Fenty Beauty', 'Nykaa', 'Charlotte Tilbury', 'Glossier', 'Amazon Beauty']
  },
  clothing: {
    brands: ['Nike', 'Adidas', 'Lululemon', 'Zara', 'H&M', 'Uniqlo', 'Gap', 'Everlane'],
    vendors: ['Zara', 'H&M', 'Uniqlo', 'Nike', 'Adidas', 'SSENSE', 'ASOS', 'Myntra', 'Nordstrom']
  },
  shoes: {
    brands: ['Nike', 'Adidas', 'New Balance', 'Puma', 'Converse', 'Vans', 'Jordan', 'Yeezy'],
    vendors: ['Nike', 'Adidas', 'Foot Locker', 'Finish Line', 'Zappos', 'DSW', 'StockX', 'GOAT']
  },
  home: {
    brands: ['IKEA', 'West Elm', 'CB2', 'Article', 'Burrow', 'Floyd', 'Pottery Barn'],
    vendors: ['IKEA', 'Wayfair', 'West Elm', 'CB2', 'Crate & Barrel', 'Pottery Barn', 'Amazon Home']
  },
  tech: {
    brands: ['Apple', 'Samsung', 'Sony', 'Bose', 'LG', 'Dell', 'HP', 'Microsoft'],
    vendors: ['Apple', 'Best Buy', 'Amazon', 'Samsung', 'B&H Photo', 'Newegg', 'Micro Center']
  },
  travel: {
    brands: ['Delta', 'United', 'American', 'Southwest', 'JetBlue', 'Hilton', 'Marriott'],
    vendors: ['Google Flights', 'Skyscanner', 'Expedia', 'Kayak', 'Booking.com', 'Airbnb', 'Hopper']
  },
  gifts: {
    brands: ['Gift cards', 'Experience gifts', 'Personalized', 'Luxury'],
    vendors: ['Amazon', 'Target', 'Nordstrom', 'Sephora', 'Best Buy', 'Etsy', 'Uncommon Goods']
  },
  flash: {
    brands: ['Various'],
    vendors: ['Amazon', 'Target', 'Best Buy', 'Costco', 'Walmart', 'eBay', 'Woot', 'Slickdeals']
  }
};

const CreateWatch = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form state
  const [category, setCategory] = useState('');
  const [productType, setProductType] = useState('');
  const [brands, setBrands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [discountThreshold, setDiscountThreshold] = useState(30);
  const [priceCap, setPriceCap] = useState('');
  const [stretchBudget, setStretchBudget] = useState('');
  const [color, setColor] = useState('');
  const [size, setSize] = useState('');
  const [notes, setNotes] = useState('');
  const [reminderType, setReminderType] = useState(null);
  const [reminderDays, setReminderDays] = useState(7);

  const selectedCategory = CATEGORIES.find(c => c.id === category);
  const categoryData = CATEGORY_DATA[category] || { brands: [], vendors: [] };

  const toggleBrand = (brand) => {
    if (brands.includes(brand)) {
      setBrands(brands.filter(b => b !== brand));
    } else {
      setBrands([...brands, brand]);
    }
  };

  const toggleVendor = (vendor) => {
    if (vendors.includes(vendor)) {
      setVendors(vendors.filter(v => v !== vendor));
    } else {
      setVendors([...vendors, vendor]);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return category !== '';
      case 1: return productType !== '';
      case 2: return true; // Vendors optional
      case 3: return discountThreshold > 0;
      case 4: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/watches', {
        category,
        product_type: productType,
        brands,
        vendors,
        discount_threshold: discountThreshold,
        price_cap: priceCap ? parseFloat(priceCap) : null,
        stretch_budget: stretchBudget ? parseFloat(stretchBudget) : null,
        color: color || null,
        size: size || null,
        notes: notes || null,
        reminder_type: reminderType,
        reminder_time_days: reminderType === 'time' ? reminderDays : null,
      });
      
      toast.success('Deal Watch created! 🐰 We\'ll hunt deals for you.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create watch');
    } finally {
      setLoading(false);
    }
  };

  // Get discount level label
  const getDiscountLabel = () => {
    if (discountThreshold >= 50) return { text: '🔥 Steal Mode', color: '#F87171' };
    if (discountThreshold >= 40) return { text: '🎯 Great Deal', color: '#FB923C' };
    if (discountThreshold >= 30) return { text: '✓ Good Target', color: '#4ADE80' };
    if (discountThreshold >= 20) return { text: 'Moderate', color: '#60A5FA' };
    return { text: 'Any discount', color: '#9CA3AF' };
  };

  const discountLabel = getDiscountLabel();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header 
        className="sticky top-0 z-40 border-b transition-colors"
        style={{ 
          backgroundColor: selectedCategory ? `${selectedCategory.color}15` : 'transparent',
          borderColor: selectedCategory ? `${selectedCategory.color}30` : 'transparent'
        }}
      >
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="font-heading font-bold flex items-center gap-2">
              <img src={GRABBIT_LOGO} alt="Grabbit" className="h-8" />
              Deal Watch
            </span>
            <div className="w-10" />
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((step, index) => (
            <div key={step} className="flex items-center flex-1">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  index < currentStep 
                    ? 'text-black' 
                    : index === currentStep 
                      ? 'border-2' 
                      : 'bg-muted text-muted-foreground'
                }`}
                style={
                  index < currentStep 
                    ? { backgroundColor: selectedCategory?.color || '#F472B6' }
                    : index === currentStep 
                      ? { borderColor: selectedCategory?.color || '#F472B6', color: selectedCategory?.color || '#F472B6' }
                      : {}
                }
              >
                {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
              </div>
              {index < STEPS.length - 1 && (
                <div 
                  className="flex-1 h-0.5 mx-2 transition-all"
                  style={{ backgroundColor: index < currentStep ? (selectedCategory?.color || '#F472B6') : '#27272A' }}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 pb-24">
        <AnimatePresence mode="wait">
          {/* Step 0: Category */}
          {currentStep === 0 && (
            <motion.div
              key="category"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center">
                <h2 className="font-heading text-2xl font-bold mb-2">What are you hunting? 🐰</h2>
                <p className="text-muted-foreground">Pick a category to start your deal watch</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {CATEGORIES.map((cat) => (
                  <motion.button
                    key={cat.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setCategory(cat.id)}
                    className={`p-6 rounded-2xl text-center transition-all border-2 ${
                      category === cat.id 
                        ? 'border-current shadow-lg' 
                        : 'border-transparent bg-card hover:border-border'
                    }`}
                    style={category === cat.id ? { 
                      borderColor: cat.color,
                      backgroundColor: `${cat.color}20`
                    } : {}}
                    data-testid={`cat-${cat.id}`}
                  >
                    <span className="text-4xl mb-3 block">{cat.icon}</span>
                    <span className="font-medium">{cat.name}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 1: Product Details */}
          {currentStep === 1 && (
            <motion.div
              key="product"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center">
                <span className="text-4xl mb-2 block">{selectedCategory?.icon}</span>
                <h2 className="font-heading text-2xl font-bold mb-2">Tell us more</h2>
                <p className="text-muted-foreground">What kind of {selectedCategory?.name.toLowerCase()} are you looking for?</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Product type *</Label>
                  <Input
                    placeholder="e.g., Running shoes, Lipstick, Laptop..."
                    value={productType}
                    onChange={(e) => setProductType(e.target.value)}
                    className="h-12 bg-input/50 border-transparent focus:border-primary rounded-xl"
                    data-testid="product-type"
                  />
                </div>

                {categoryData.brands.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Preferred brands (optional)</Label>
                    <div className="flex flex-wrap gap-2">
                      {categoryData.brands.map((brand) => (
                        <button
                          key={brand}
                          onClick={() => toggleBrand(brand)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            brands.includes(brand)
                              ? 'text-black'
                              : 'bg-muted hover:bg-muted/80'
                          }`}
                          style={brands.includes(brand) ? { backgroundColor: selectedCategory?.color } : {}}
                        >
                          {brand}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Color (optional)</Label>
                    <Input
                      placeholder="Any"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="h-12 bg-input/50 border-transparent focus:border-primary rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Size (optional)</Label>
                    <Input
                      placeholder="Any"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      className="h-12 bg-input/50 border-transparent focus:border-primary rounded-xl"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Vendors */}
          {currentStep === 2 && (
            <motion.div
              key="vendors"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center">
                <Store className="w-12 h-12 mx-auto mb-3 text-primary" />
                <h2 className="font-heading text-2xl font-bold mb-2">Where to hunt?</h2>
                <p className="text-muted-foreground">Select stores you want us to watch (optional)</p>
              </div>

              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {categoryData.vendors.map((vendor) => (
                    <button
                      key={vendor}
                      onClick={() => toggleVendor(vendor)}
                      className={`px-4 py-2 rounded-xl text-sm transition-all flex items-center gap-2 ${
                        vendors.includes(vendor)
                          ? 'text-black shadow-md'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                      style={vendors.includes(vendor) ? { backgroundColor: selectedCategory?.color } : {}}
                    >
                      <Store className="w-4 h-4" />
                      {vendor}
                    </button>
                  ))}
                </div>

                {vendors.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    We'll search all available stores if none selected
                  </p>
                )}

                {vendors.length > 0 && (
                  <div className="glass rounded-xl p-4">
                    <p className="text-sm text-muted-foreground mb-2">Selected stores:</p>
                    <p className="font-medium">{vendors.join(', ')}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 3: Alert Settings */}
          {currentStep === 3 && (
            <motion.div
              key="alert"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center">
                <h2 className="font-heading text-2xl font-bold mb-2">When to alert you?</h2>
                <p className="text-muted-foreground">Set your deal criteria</p>
              </div>

              <div className="space-y-6">
                {/* Discount Threshold Slider */}
                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="font-medium">Minimum discount</Label>
                    <div className="text-right">
                      <span 
                        className="text-2xl font-bold"
                        style={{ color: discountLabel.color }}
                      >
                        {discountThreshold}%+
                      </span>
                      <p className="text-xs" style={{ color: discountLabel.color }}>
                        {discountLabel.text}
                      </p>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="80"
                    step="5"
                    value={discountThreshold}
                    onChange={(e) => setDiscountThreshold(parseInt(e.target.value))}
                    className="w-full h-3 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, ${selectedCategory?.color} 0%, ${selectedCategory?.color} ${(discountThreshold - 10) / 70 * 100}%, #27272A ${(discountThreshold - 10) / 70 * 100}%, #27272A 100%)`
                    }}
                    data-testid="discount-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-2">
                    <span>10%</span>
                    <span>30%</span>
                    <span>50% 🔥</span>
                    <span>80%</span>
                  </div>
                </div>

                {/* Budget Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Max budget (hard stop)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="e.g., 120"
                        value={priceCap}
                        onChange={(e) => setPriceCap(e.target.value)}
                        className="h-12 pl-11 bg-input/50 border-transparent focus:border-primary rounded-xl"
                        data-testid="price-cap"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Stretch budget (soft alert)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="e.g., 200"
                        value={stretchBudget}
                        onChange={(e) => setStretchBudget(e.target.value)}
                        className="h-12 pl-11 bg-input/50 border-transparent focus:border-primary rounded-xl"
                        data-testid="stretch-budget"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Alert if a ${stretchBudget || '200'} item drops to your budget
                    </p>
                  </div>
                </div>

                {/* Reminder Type */}
                <div className="space-y-3">
                  <Label className="text-sm text-muted-foreground">Smart reminders (optional)</Label>
                  <div className="space-y-2">
                    <button
                      onClick={() => setReminderType(reminderType === 'time' ? null : 'time')}
                      className={`w-full p-4 rounded-xl text-left flex items-center gap-4 transition-all ${
                        reminderType === 'time' ? 'border-2' : 'bg-muted'
                      }`}
                      style={reminderType === 'time' ? { borderColor: selectedCategory?.color, backgroundColor: `${selectedCategory?.color}15` } : {}}
                    >
                      <Clock className="w-5 h-5" style={{ color: reminderType === 'time' ? selectedCategory?.color : undefined }} />
                      <div>
                        <p className="font-medium">Time-based</p>
                        <p className="text-sm text-muted-foreground">Remind me to check again later</p>
                      </div>
                    </button>
                    
                    {reminderType === 'time' && (
                      <motion.div 
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        className="ml-9 mt-2"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Check again in</span>
                          <select 
                            value={reminderDays}
                            onChange={(e) => setReminderDays(parseInt(e.target.value))}
                            className="bg-input rounded-lg px-3 py-1 text-sm"
                          >
                            <option value={3}>3 days</option>
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                          </select>
                        </div>
                      </motion.div>
                    )}

                    <button
                      onClick={() => setReminderType(reminderType === 'price' ? null : 'price')}
                      className={`w-full p-4 rounded-xl text-left flex items-center gap-4 transition-all ${
                        reminderType === 'price' ? 'border-2' : 'bg-muted'
                      }`}
                      style={reminderType === 'price' ? { borderColor: selectedCategory?.color, backgroundColor: `${selectedCategory?.color}15` } : {}}
                    >
                      <DollarSign className="w-5 h-5" style={{ color: reminderType === 'price' ? selectedCategory?.color : undefined }} />
                      <div>
                        <p className="font-medium">Price-based</p>
                        <p className="text-sm text-muted-foreground">Alert at lowest price in 30 days</p>
                      </div>
                    </button>

                    <button
                      onClick={() => setReminderType(reminderType === 'expert' ? null : 'expert')}
                      className={`w-full p-4 rounded-xl text-left flex items-center gap-4 transition-all ${
                        reminderType === 'expert' ? 'border-2' : 'bg-muted'
                      }`}
                      style={reminderType === 'expert' ? { borderColor: selectedCategory?.color, backgroundColor: `${selectedCategory?.color}15` } : {}}
                    >
                      <UserIcon className="w-5 h-5" style={{ color: reminderType === 'expert' ? selectedCategory?.color : undefined }} />
                      <div>
                        <p className="font-medium">Expert-based</p>
                        <p className="text-sm text-muted-foreground">Notify when an expert posts similar</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Confirm */}
          {currentStep === 4 && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center">
                <h2 className="font-heading text-2xl font-bold mb-2">Ready to hunt! 🐰</h2>
                <p className="text-muted-foreground">Review your deal watch</p>
              </div>

              {/* Summary Card */}
              <div 
                className="rounded-2xl overflow-hidden border-2"
                style={{ borderColor: `${selectedCategory?.color}40` }}
              >
                <div 
                  className="p-6"
                  style={{ backgroundColor: `${selectedCategory?.color}15` }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl">{selectedCategory?.icon}</span>
                    <div>
                      <h3 className="font-bold text-lg">{productType}</h3>
                      <p className="text-sm text-muted-foreground">{selectedCategory?.name}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {brands.length > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <Tag className="w-4 h-4 text-muted-foreground" />
                        <span>Brands: {brands.join(', ')}</span>
                      </div>
                    )}
                    {vendors.length > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <Store className="w-4 h-4 text-muted-foreground" />
                        <span>Stores: {vendors.join(', ')}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Bell className="w-4 h-4 text-muted-foreground" />
                      <span>
                        Alert at: <strong style={{ color: discountLabel.color }}>
                          {discountThreshold}%+ off {discountLabel.text}
                        </strong>
                      </span>
                    </div>
                    {priceCap && (
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="w-4 h-4 text-muted-foreground" />
                        <span>Max budget: <strong>${priceCap}</strong></span>
                      </div>
                    )}
                    {stretchBudget && (
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="w-4 h-4 text-muted-foreground" />
                        <span>Stretch budget: <strong>${stretchBudget}</strong></span>
                      </div>
                    )}
                    {reminderType && (
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span>
                          {reminderType === 'time' && `Remind in ${reminderDays} days`}
                          {reminderType === 'price' && 'Alert at 30-day low'}
                          {reminderType === 'expert' && 'Notify on expert posts'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-card">
                  <p className="text-sm text-muted-foreground text-center">
                    🐰 Grabbit will hunt deals and notify you when we find matches!
                  </p>
                </div>
              </div>

              <Textarea
                placeholder="Any notes for yourself... (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-input/50 border-transparent focus:border-primary rounded-xl resize-none"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 glass border-t border-border/50 p-4">
        <div className="max-w-2xl mx-auto flex gap-4">
          <Button 
            variant="outline"
            onClick={handleBack}
            className="flex-1 h-12 rounded-xl"
            data-testid="prev-btn"
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          {currentStep < STEPS.length - 1 ? (
            <Button 
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex-1 h-12 rounded-xl text-black transition-all"
              style={{ backgroundColor: selectedCategory?.color || '#F472B6' }}
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={!canProceed() || loading}
              className="flex-1 h-12 rounded-xl font-bold transition-all bg-gradient-to-r from-pink-500 to-purple-600 text-white"
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  🐰 Start Hunting!
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateWatch;
